import { useEffect, useMemo, useRef, useState } from 'react';
import {
  rovingRadioTabIndex,
  rovingTabIndex,
  useRovingRadioGroup,
} from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  StarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Rating, TagMode, TaxonomyTag } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { fetchTaxonomy } from '../taxonomy/taxonomy-api';
import { ApiError } from './api';

/**
 * Panneau « Recherche » : UNIQUE point d'entrée de recherche de la galerie. Affine par
 * NOM DE FICHIER, NOTE minimale (0–5★) et TAGS (multi + bascule « Au moins un » / « Tous »).
 * Ces critères se combinent EN ET entre eux ET avec le reste de la galerie
 * (onglet/dossier/filtre Type…). Le filtre par TYPE de média n'est PLUS ici : il a une
 * SOURCE UNIQUE, la section « Type » du menu « Trier & filtrer » (plus de double réglage
 * contradictoire). Le champ « nom de fichier » (jadis dans la barre d'outils) vit ICI :
 * tout est appliqué d'un coup sur « Appliquer » (rien n'est appliqué en direct).
 *
 * Vraie boîte de dialogue (`role="dialog"` + `aria-modal`) calquée sur `BulkTagModal` :
 * focus initial, restauration du focus, verrou de défilement, Échap ferme, clic sur le
 * fond ferme. Le caller l'enveloppe dans un `<Portal>` (l'en-tête porte `backdrop-blur`).
 *
 * Le vocabulaire de tags est chargé PAR le panneau (au montage) depuis la taxonomie
 * (`GET /api/libraries/:id/taxonomy`). « Appliquer » remonte les critères choisis (le
 * caller les pousse dans l'état/URL galerie) ; « Réinitialiser » vide TOUS les critères
 * dans le formulaire (sans fermer). Les cases cochées le sont par une COCHE +
 * `aria-checked`, les étoiles par leur FORME (pleine/vide) — jamais la couleur seule.
 */

/** Critères de recherche (forme partagée avec l'état galerie). */
export interface AdvancedCriteria {
  /** Recherche par NOM DE FICHIER (`q`). Chaîne vide = aucun filtre de nom. */
  filename: string;
  ratingMin: number | null;
  tagIds: string[];
  tagMode: TagMode;
}

interface AdvancedSearchModalProps {
  libraryId: string;
  /** Valeurs initiales (état courant de la galerie). */
  initial: AdvancedCriteria;
  /** Applique les critères choisis (le caller les pousse dans l'état/URL) puis ferme. */
  onApply: (criteria: AdvancedCriteria) => void;
  onClose: () => void;
}

/** Étoiles cliquables du sélecteur de note minimale (0 = « toutes »). */
const STARS: readonly Rating[] = [1, 2, 3, 4, 5];

export function AdvancedSearchModal({
  libraryId,
  initial,
  onApply,
  onClose,
}: AdvancedSearchModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  // Copie ÉDITABLE locale des critères (rien n'est appliqué avant « Appliquer »).
  const [filename, setFilename] = useState(initial.filename);
  const [ratingMin, setRatingMin] = useState<Rating>((initial.ratingMin ?? 0) as Rating);
  const [tagIds, setTagIds] = useState<Set<string>>(new Set(initial.tagIds));
  const [tagMode, setTagMode] = useState<TagMode>(initial.tagMode);
  // Deux radiogroups de CRITÈRES locaux : rien n'est requêté avant « Rechercher »,
  // la sélection SUIT donc le focus (motif ARIA standard).
  const onRatingKeyDown = useRovingRadioGroup('auto');
  const onTagModeKeyDown = useRovingRadioGroup('auto');
  // Taxonomie (chargée au montage).
  const [tags, setTags] = useState<TaxonomyTag[] | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  const applyRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: applyRef, onEscape: onClose });

  // Charge le vocabulaire existant au montage (annulable au démontage).
  useEffect(() => {
    const ctrl = new AbortController();
    void (async (): Promise<void> => {
      try {
        const result = await fetchTaxonomy(libraryId, ctrl.signal);
        setTags(result.tags);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(
          err instanceof ApiError && err.status === 403
            ? t('bulkTag.errorForbidden')
            : t('bulkTag.loadError'),
        );
        setTags([]);
      }
    })();
    return () => ctrl.abort();
  }, [libraryId, t]);

  // Filtre client par étiquette (insensible à la casse/aux accents).
  const normalized = search.trim().toLocaleLowerCase();
  const filtered = useMemo(() => {
    if (tags === null) return [];
    if (normalized === '') return tags;
    return tags.filter((tag) =>
      tag.label.toLocaleLowerCase().normalize('NFD').includes(normalized.normalize('NFD')),
    );
  }, [tags, normalized]);

  const toggleTag = (id: string): void => {
    setTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = (): void => {
    setFilename('');
    setRatingMin(0 as Rating);
    setTagIds(new Set());
    setTagMode('any');
    setSearch('');
  };

  const apply = (): void => {
    onApply({
      filename,
      ratingMin: ratingMin === 0 ? null : ratingMin,
      tagIds: [...tagIds],
      tagMode,
    });
  };

  const loading = tags === null;
  const hasTags = tags !== null && tags.length > 0;

  return (
    <ModalOverlay onDismiss={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('advancedSearch.title')}
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <AdjustmentsHorizontalIcon className="h-6 w-6 text-muted" aria-hidden="true" />
            {t('advancedSearch.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title={t('advancedSearch.cancel')}
            aria-label={t('advancedSearch.cancel')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-5 overflow-y-auto p-4 text-sm text-fg">
          <p className="text-muted">{t('advancedSearch.intro')}</p>

          {/* Nom de fichier (optionnel) — appliqué sur « Appliquer », pas en direct. */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">{t('advancedSearch.filenameSection')}</h3>
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                aria-label={t('advancedSearch.filenameSection')}
                placeholder={t('advancedSearch.filenamePlaceholder')}
                className="h-9 w-full rounded-md border border-border-strong bg-surface ps-9 pe-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              />
            </div>
          </section>

          {/* Note minimale (0..5 ; 0 = toutes). */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">{t('advancedSearch.ratingSection')}</h3>
            <div className="flex items-center gap-2">
              <div
                role="radiogroup"
                aria-label={t('advancedSearch.ratingSection')}
                onKeyDown={onRatingKeyDown}
                className="inline-flex items-center gap-0.5"
              >
                {STARS.map((n) => {
                  const filled = n <= ratingMin;
                  // Re-cliquer l'étoile courante remet à 0 (= « toutes »).
                  const target: Rating = n === ratingMin ? 0 : n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={n === ratingMin}
                      // Note 0 (« toutes ») : rien de coché → la 1re étoile est le point d'entrée.
                      tabIndex={rovingRadioTabIndex(n === ratingMin, n === STARS[0], ratingMin > 0)}
                      onClick={() => setRatingMin(target)}
                      aria-label={interpolate(t('rating.star'), { n })}
                      title={interpolate(t('rating.star'), { n })}
                      className="rounded p-0.5 text-fg hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      <StarIcon
                        className="h-7 w-7"
                        fill={filled ? 'currentColor' : 'none'}
                        aria-hidden="true"
                      />
                    </button>
                  );
                })}
              </div>
              {ratingMin > 0 && (
                <button
                  type="button"
                  onClick={() => setRatingMin(0 as Rating)}
                  className="rounded-md px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('rating.clear')}
                </button>
              )}
            </div>
            {/* État en texte (jamais la forme/couleur seule). */}
            <p className="text-xs text-muted" aria-live="polite">
              {ratingMin === 0
                ? t('advancedSearch.ratingAny')
                : interpolate(t('advancedSearch.ratingAtLeast'), { rating: ratingMin })}
            </p>
          </section>

          {/* Tags (multi + bascule tous / au moins un). */}
          <section className="flex min-h-0 flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">{t('advancedSearch.tagsSection')}</h3>

            {/* Bascule « Au moins un » / « Tous ». */}
            <div
              role="radiogroup"
              aria-label={t('advancedSearch.tagModeLabel')}
              onKeyDown={onTagModeKeyDown}
              className="inline-flex w-fit rounded-md border border-border p-0.5"
            >
              {(['any', 'all'] as const).map((mode) => {
                const active = tagMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    tabIndex={rovingTabIndex(active)}
                    onClick={() => setTagMode(mode)}
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
                      active ? 'bg-brand text-brand-fg' : 'text-muted hover:text-fg'
                    }`}
                  >
                    {active && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
                    {mode === 'any'
                      ? t('advancedSearch.tagModeAny')
                      : t('advancedSearch.tagModeAll')}
                  </button>
                );
              })}
            </div>

            {/* Recherche client par nom de tag. */}
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!hasTags}
                aria-label={t('bulkTag.searchLabel')}
                placeholder={t('bulkTag.searchPlaceholder')}
                className="h-9 w-full rounded-md border border-border-strong bg-surface ps-9 pe-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              />
            </div>

            {/* Liste des tags existants (multi-sélection). */}
            <div
              className="max-h-56 min-h-0 overflow-y-auto"
              role="group"
              aria-label={t('bulkTag.listLabel')}
            >
              {loading ? (
                <p className="flex items-center gap-2 py-4 text-muted" role="status">
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                  {t('bulkTag.loading')}
                </p>
              ) : !hasTags ? (
                <p className="py-4 text-muted">{t('bulkTag.empty')}</p>
              ) : filtered.length === 0 ? (
                <p className="py-4 text-muted">{t('bulkTag.noMatch')}</p>
              ) : (
                <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                  {filtered.map((tag) => {
                    const isChosen = tagIds.has(tag.id);
                    return (
                      <li key={tag.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-2">
                          <input
                            type="checkbox"
                            checked={isChosen}
                            onChange={() => toggleTag(tag.id)}
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                              isChosen
                                ? 'border-brand bg-brand text-brand-fg'
                                : 'border-border bg-surface'
                            }`}
                          >
                            {isChosen && <CheckIcon className="h-4 w-4" />}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-fg">{tag.label}</span>
                          <span className="shrink-0 text-xs text-muted">
                            {interpolate(
                              t('bulkTag.usageCount'),
                              { count: tag.usageCount },
                              locale,
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Nombre de tags choisis (texte, pour les lecteurs d'écran). */}
            <p className="text-xs text-muted" aria-live="polite">
              {interpolate(t('bulkTag.chosenCount'), { count: tagIds.size }, locale)}
            </p>

            {loadError && (
              <p role="alert" className="flex items-start gap-2 text-danger">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{loadError}</span>
              </p>
            )}
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={reset}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('advancedSearch.reset')}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('advancedSearch.cancel')}
            </button>
            <button
              ref={applyRef}
              type="button"
              onClick={apply}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('advancedSearch.apply')}
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
